import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StatusCode } from '@hungryroot/rest';
import { type OrderFeedbackQuestionType } from '@hungryroot/rest/api/v2/customers/[id]/order_feedback_questions/get/get-customer-order-feedback-questions';
import { postCustomerOrderFeedbackQuestions } from '@hungryroot/rest/api/v2/customers/[id]/order_feedback_questions/post/post-customer-order-feedback-questions';
import { getOrderQueryKey } from './useOrderFeedbackQuestion';

interface UseOrderFeedbackAnswers {
  orderId: number;
  answers: {
    questionId: number;
    answerId: number;
  }[];
}

/**
 * Posts answers to order feedback questions.
 * Invalidates the query for the order feedback question answered after posting the answers.
 */
export function useOrderFeedbackAnswer({
  type,
}: {
  type: OrderFeedbackQuestionType;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, answers }: UseOrderFeedbackAnswers) => {
      const res = await Promise.all(
        answers.map((answer) =>
          postCustomerOrderFeedbackQuestions({
            orderId,
            ...answer,
          })
        )
      );

      res.forEach((r) => {
        if (r.status !== StatusCode.SuccessCreated) {
          throw new Error(JSON.stringify(r.data, null, 2));
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getOrderQueryKey(type) });
    },
  });
}
